import type { ProductType } from "@1js/pl-card-components";
import type { PositioningImperativeRef } from "@fluentui/react-components";
import {
  Add24Filled,
  Add24Regular,
  CloudAdd24Filled,
  CloudAdd24Regular,
  bundleIcon,
} from "@fluentui/react-icons";
import React from "react";
import type {
  HandoffFile,
  RecentFiles,
} from "../../services/workingsetfiles-client.web";

export type HandoffFileSelectionMenuProps = {
  product: ProductType | string;
  isOpen: boolean;
  onOpenChange: () => void;
  onAction: (fileId: string | undefined) => void;
  openRef: React.MutableRefObject<null>;
  recentFiles: RecentFiles;
};

export type HandoffFileSelectionMenuResult = {
  recentFilesWithActions: HandoffFileWithAction[] | undefined;
  positioningRef: React.MutableRefObject<PositioningImperativeRef | null>;
  CloudAdd24Icon: React.FC;
  Add24Icon: React.FC;
  onNewDocument: () => void;
  onCloudDocument: () => void;
};

export type HandoffFileWithAction = {
  fileName: string;
  onAction: () => void;
};

const CloudAdd24Icon = bundleIcon(CloudAdd24Filled, CloudAdd24Regular);
const Add24Icon = bundleIcon(Add24Filled, Add24Regular);

export const useHandoffFileSelectionMenu = (
  props: HandoffFileSelectionMenuProps
): HandoffFileSelectionMenuResult => {
  const { product, onAction, openRef, recentFiles } = props;
  const recentFilesWithActions: HandoffFileWithAction[] | undefined =
    React.useMemo(() => {
      if (["Word", "Excel", "PowerPoint"].includes(product)) {
        return recentFiles[product].map((file: HandoffFile) => ({
          fileName: file.fileName,
          onAction: () => onAction(file.fileId),
        }));
      }
    }, [onAction, product, recentFiles]);

  const positioningRef = React.useRef<PositioningImperativeRef>(null);

  React.useEffect(() => {
    if (openRef.current) {
      positioningRef.current?.setTarget(openRef.current);
    }
  }, [openRef, positioningRef]);

  const onNewDocument = React.useCallback(() => {
    onAction(undefined);
  }, [onAction]);

  // TODO(anbal): Add MSAL.JS to retrieve token via Auth Spa Code and add file picker selection
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onCloudDocument = React.useCallback(() => {}, []);

  return {
    recentFilesWithActions,
    positioningRef,
    CloudAdd24Icon,
    Add24Icon,
    onNewDocument,
    onCloudDocument,
  };
};
